import React, { useState } from 'react';
import { toast as Toast } from 'react-toastify';
import { Button, Card, CardBody, CardHeader, Spinner } from 'reactstrap';

import { startCigarFingerprintJob } from '@/api/cigar-matching';
import ModalDialog from '@/components/modal-dialog';
import ToastContent from '@/components/toasts/content';

const getApiErrorMessage = (error: any) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  'The catalog fingerprint job could not be started.';

function BulkFingerprintJobs() {
  const [maximumImages, setMaximumImages] = useState(3);
  const [force, setForce] = useState(false);
  const [isStarting, setIsStarting] = useState(false);

  const startJob = async () => {
    if (isStarting) return;
    if (!Number.isInteger(maximumImages) || maximumImages <= 0) {
      Toast.error(
        <ToastContent
          title="Invalid image limit"
          message="Maximum images must be a positive whole number."
        />,
        { position: 'top-right' }
      );
      return;
    }

    try {
      setIsStarting(true);
      const response = await startCigarFingerprintJob({
        force,
        maximumImages,
      });
      const jobDetail = response.job?.id
        ? ` Job ${response.job.id} is ${response.job.status || 'queued'}.`
        : '';
      Toast.info(
        <ToastContent
          title="Fingerprint job started"
          message={`${response.message || 'The server accepted the bulk fingerprint job.'}${jobDetail}`}
        />,
        { position: 'top-right' }
      );
    } catch (error) {
      Toast.error(
        <ToastContent
          title="Unable to start fingerprint job"
          message={getApiErrorMessage(error)}
        />,
        { position: 'top-right' }
      );
    } finally {
      setIsStarting(false);
    }
  };

  const submitJob = () => {
    if (!force) {
      startJob();
      return;
    }
    ModalDialog.show({
      title: 'Regenerate catalog fingerprints?',
      type: 'warning',
      message:
        'This bulk job will replace existing fingerprints and may perform paid vision inference across the catalog.',
      buttons: [
        { label: 'Cancel', onClick: () => ModalDialog.close() },
        {
          label: 'Regenerate Fingerprints',
          color: 'warning',
          onClick: () => {
            ModalDialog.close();
            startJob();
          },
        },
      ],
    });
  };

  return (
    <Card className="recognition-fingerprint-jobs">
      <CardHeader>
        <div>
          <h5>Bulk fingerprints</h5>
          <p>Generate visual fingerprints from catalog reference images.</p>
        </div>
      </CardHeader>
      <CardBody>
        <label>
          <span>Maximum images per cigar</span>
          <input
            type="number"
            className="form-control form-control-sm"
            min={1}
            step={1}
            value={maximumImages}
            disabled={isStarting}
            onChange={(event) => setMaximumImages(Number(event.target.value))}
          />
        </label>
        <label className="recognition-fingerprint-force">
          <input
            type="checkbox"
            checked={force}
            disabled={isStarting}
            onChange={(event) => setForce(event.target.checked)}
          />
          <span>Regenerate existing fingerprints</span>
        </label>
        <Button
          color={force ? 'warning' : 'primary'}
          onClick={submitJob}
          disabled={isStarting}
        >
          {isStarting ? (
            <>
              <Spinner size="sm" /> Starting…
            </>
          ) : (
            'Generate Fingerprints'
          )}
        </Button>
      </CardBody>
    </Card>
  );
}

export default BulkFingerprintJobs;
